import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, MessageCircle, Archive, ChevronDown, ChevronUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import BottomNav from "@/components/BottomNav";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Conversation {
  id: string;
  subject: string | null;
  status: "open" | "pending" | "closed";
  last_message_preview: string | null;
  last_message_at: string | null;
  unread_count_user: number;
  service_id: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
  service?: {
    id: string;
    name: string;
  } | null;
}

export default function ConversationsListPage() {
  const navigate = useNavigate();
  const { user, isAgent } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Redirect agents
  useEffect(() => {
    if (isAgent) {
      navigate("/agent", { replace: true });
    }
  }, [isAgent, navigate]);

  // Fetch conversations
  useEffect(() => {
    if (!user) return;

    const fetchConversations = async () => {
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          *,
          service:services(id, name)
        `)
        .eq("user_id", user.id)
        .order("last_message_at", { ascending: false });

      if (!error && data) {
        setConversations(data as Conversation[]);
      }
      setLoading(false);
    };

    fetchConversations();

    // Refetch when window regains focus (user comes back to page)
    const handleFocus = () => {
      fetchConversations();
    };
    window.addEventListener('focus', handleFocus);

    // Real-time subscription
    const channel = supabase
      .channel("user-conversations")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversations",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[ConversationsList] Realtime update received:', payload);
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener('focus', handleFocus);
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Separate active and archived conversations
  const activeConversations = conversations.filter(c => !c.is_archived);
  const archivedConversations = conversations.filter(c => c.is_archived);

  const getStatusBadge = (status: string, isArchived?: boolean) => {
    if (isArchived) {
      return null; // We show archive badge separately
    }
    switch (status) {
      case "open":
        return (
          <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[10px]">
            <MessageCircle size={10} className="mr-1" />
            En cours
          </Badge>
        );
      default:
        return null;
    }
  };

  const renderConversationCard = (conv: Conversation) => (
    <button
      key={conv.id}
      onClick={() => navigate(`/chat/${conv.id}`)}
      className={`w-full flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-gold transition-all duration-200 text-left touch-feedback ${conv.is_archived ? 'opacity-70' : ''}`}
    >
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <span className="text-primary font-serif font-semibold text-sm sm:text-base">C</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
          {conv.service?.name && (
            <span className="font-serif font-medium text-foreground text-xs sm:text-sm">
              {conv.service.name}
            </span>
          )}
          {getStatusBadge(conv.status, conv.is_archived)}
          {conv.is_archived && (
            <Badge variant="outline" className="bg-muted text-muted-foreground border-muted text-[10px]">
              <Archive size={10} className="mr-1" />
              Traitée
            </Badge>
          )}
          {conv.unread_count_user > 0 && (
            <Badge className="bg-primary text-primary-foreground text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 min-w-[16px] sm:min-w-[18px] h-[16px] sm:h-[18px]">
              {conv.unread_count_user}
            </Badge>
          )}
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground truncate">
          {conv.last_message_preview || "Nouvelle conversation"}
        </p>
        {conv.is_archived && (
          <p className="text-[9px] sm:text-[10px] text-muted-foreground/70 mt-1 italic">
            Répondez pour réactiver cette conversation
          </p>
        )}
        {conv.last_message_at && !conv.is_archived && (
          <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(conv.last_message_at), {
              addSuffix: true,
              locale: fr,
            })}
          </p>
        )}
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pb-20">
        <header className="px-6 pt-8 pb-4">
          <Skeleton className="h-8 w-48" />
        </header>
        <main className="flex-1 px-6 space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pb-20 bg-background">
      {/* Header */}
      <header 
        className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4"
        style={{ paddingTop: 'max(1.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <h1 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Messages
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Vos conversations avec le concierge
            </p>
          </div>
          <Button 
            variant="gold" 
            size="icon"
            onClick={() => navigate("/chat/new")}
            className="shrink-0 h-9 w-9 sm:h-10 sm:w-10"
          >
            <Plus size={18} className="sm:hidden" />
            <Plus size={20} className="hidden sm:block" />
          </Button>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <MessageCircle size={28} className="sm:hidden text-primary" />
                <MessageCircle size={32} className="hidden sm:block text-primary" />
              </div>
              <h2 className="font-serif text-base sm:text-lg font-medium text-foreground mb-2">
                Aucune conversation
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4 max-w-xs">
                Démarrez une nouvelle conversation avec votre concierge
              </p>
              <Button variant="gold" size="sm" className="sm:size-default" onClick={() => navigate("/chat/new")}>
                <Plus size={16} className="mr-2" />
                Nouvelle demande
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active Conversations */}
              {activeConversations.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                    Conversations actives ({activeConversations.length})
                  </h2>
                  {activeConversations.map(renderConversationCard)}
                </div>
              )}

              {/* Archived Conversations - Collapsible */}
              {archivedConversations.length > 0 && (
                <Collapsible open={showArchived} onOpenChange={setShowArchived}>
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:bg-muted/50 rounded-lg transition-colors">
                      <span className="flex items-center gap-2">
                        <Archive size={14} />
                        Conversations traitées ({archivedConversations.length})
                      </span>
                      {showArchived ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 sm:space-y-3 mt-2">
                    {archivedConversations.map(renderConversationCard)}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* No active conversations message */}
              {activeConversations.length === 0 && archivedConversations.length > 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-3">
                    <MessageCircle size={24} className="text-success" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Toutes vos demandes ont été traitées
                  </p>
                  <Button variant="gold" size="sm" onClick={() => navigate("/chat/new")}>
                    <Plus size={16} className="mr-2" />
                    Nouvelle demande
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
